import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { FileAPI } from "../api/fileApi";
import { setFileError, setFiles } from "../redux/file-upload/fileUploadSlice";
import {
  extractFileNameFromUrl,
  sanitizeNonEmptyStrings
} from "../helpers/string_helpers";
import { uploadFiles } from "../redux/file-upload/fileUploadThunk";

export const useUploadByLink = () => {
  const dispatch = useDispatch();

  type LinkPickResult = {
    file: File | null;
    error?: string;
    url: string;
    filePath?: string;
  };

  const pickFilesByLink = useCallback(
    async (
      url: string,
      topicId?: string,
      caseId?: string
    ): Promise<LinkPickResult> => {
      if (!url || typeof url !== "string") {
        return { file: null, error: "Please provide a valid URL", url };
      }

      try {
        const { fileName, blob } = await FileAPI.urlToPdf(
          { url, topic_id: topicId, case_id: caseId },
          "binary"
        );
        const file = new File(
          [blob],
          fileName || extractFileNameFromUrl(url, "document.pdf"),
          {
            type: "application/pdf"
          }
        );
        return { file, url };
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";

        const failedFileName = extractFileNameFromUrl(url, "document.pdf");
        const failedFile = new File([], failedFileName, {
          type: "application/pdf"
        });

        dispatch(
          setFileError({ fileName: failedFileName, error: errorMessage })
        );

        return { file: failedFile, error: errorMessage, url };
      }
    },
    [dispatch]
  );

  const uploadByLinks = useCallback(
    async (
      entries: Array<
        string | { url: string; topicId?: string; caseId?: string }
      >,
      onFiles?: (files: FileList) => void
    ): Promise<{ results: LinkPickResult[]; files: File[] }> => {
      const normalized: { url: string; topicId?: string; caseId?: string }[] =
        entries.map((e) =>
          typeof e === "string"
            ? { url: e }
            : { url: e.url, topicId: e.topicId, caseId: e.caseId }
        );

      const sanitized = normalized
        .filter((e) => typeof e.url === "string")
        .map((e) => ({ ...e, url: e.url.trim() }))
        .filter((e) => e.url.length > 0);

      if (sanitized.length === 0) {
        return { results: [], files: [] };
      }

      const results: LinkPickResult[] = await Promise.all(
        sanitized.map(({ url, topicId, caseId }) =>
          pickFilesByLink(url, topicId, caseId)
        )
      );

      const files = results
        .filter((r) => r.file && !r.error)
        .map((r) => r.file!) as File[];

      if (files.length > 0) {
        // Replace placeholders with real files and trigger standard upload flow
        dispatch(setFiles(files));
        dispatch(uploadFiles() as any);
      }

      if (onFiles) {
        const dt = new DataTransfer();
        files.forEach((f) => dt.items.add(f));
        onFiles(dt.files);
      }

      return { results, files };
    },
    [pickFilesByLink, dispatch]
  );

  return { pickFilesByLink, uploadByLinks };
};
